import { PButtonPure } from "@porsche-design-system/components-react";
import { AuthContext } from "MainApp/assets/utils/context/AuthContext";
import { useContext } from "react";
import styled from "styled-components";

// Styled wrapper for Porsche button.

export const StyledPButtonPure = styled(PButtonPure).attrs((props) => ({
  theme: props.pdsTheme ?? (props.isTuk ? "light" : "dark"),
  alignLabel: props.alignLabel ?? "center",
  disabled: Boolean(props.disabled), // Ensure boolean propagation
}))`

  :where(&) {
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
    tab-size: 4;

    /* Keep PDS theme tokens aligned with our button fallbacks so shadow-DOM labels stay readable. */
    --pds-theme-light-primary: #fbfcff;
    --pds-theme-dark-primary: #010205;
    --pds-theme-light-contrast-high: #010205;
    --pds-theme-light-contrast-medium: #535457;
    --pds-theme-dark-contrast-high: #fbfcff;
    --pds-theme-dark-contrast-medium: #d8dce7;
    --background: #fff;

    -webkit-font-smoothing: antialiased;

    --pds-internal-grid-outer-column: calc(
      clamp(32px, 2.75vw + 23px, 76px) - clamp(16px, 1.25vw + 12px, 36px)
    ) !important;
    --pds-internal-grid-margin: calc(
      clamp(32px, 2.75vw + 23px, 76px) * -1
    ) !important;
    --pds-internal-grid-width-min: auto !important;
    --pds-internal-grid-width-max: none !important;
    --p-modal-width: 80svw;

    visibility: inherit;
    pointer-events: auto;

    --background-color: #0000;

    color-scheme: light dark !important;
    white-space: nowrap;
    justify-content: center !important;
    width: ${(props) => (props.stretch ? "100%" : "auto")};
    max-width: 100%;
    min-width: min-content;
    box-sizing: border-box;
    text-align: start;
    appearance: none;
    text-decoration: none;
    font: normal normal 400 1rem / calc(6px + 2.125ex) "Porsche Next",
      "Arial Narrow", Arial, "Heiti SC", SimHei, sans-serif;
    overflow-wrap: break-word;
    hyphens: auto;

    border: ${(props) =>
      props.disabled
        ? "2px solid #d8dce7"
        : !props.isTuk
        ? "2px solid #010205"
        : "2px solid #facc15"};
    border-radius: 4px;
    transform: translate3d(0, 0, 0);
    background-color: ${(props) =>
      props.disabled ? "#d8dce7" : !props.isTuk ? "#010205" : "#facc15"};
    color: ${(props) =>
      props.disabled
        ? "#535457" // o la que quieras
        : props.isTuk
        ? "#010205"
        : "#FBFCFF"};
    transition: background-color var(--p-transition-duration, 0.25s)
        cubic-bezier(0.25, 0.1, 0.25, 1),
      border-color var(--p-transition-duration, 0.25s)
        cubic-bezier(0.25, 0.1, 0.25, 1),
      color var(--p-transition-duration, 0.25s) cubic-bezier(0.25, 0.1, 0.25, 1);

    padding: calc(var(--p-internal-button-scaling, 1) * 0.8125 * 16px);
    gap: clamp(
      2px,
      calc(var(--p-internal-button-scaling, 1) * 0.5 * 16px),
      16px
    );
    cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
    margin: 0;
  }
`;

const CButton = ({
  active,
  alignLabel,
  aria,
  disabled,
  hideLabel,
  icon = "none",
  iconSource,
  loading,
  name,
  size,
  stretch,
  theme,
  type,
  underline,
  value,
  ///
  suppressHydrationWarning,
  autoFocus,
  className,
  dir,
  hidden,
  id,
  lang,
  slot,
  style,
  tabIndex,
  title,
  translate,
  //
  children,
  onClick,
}) => {
  const { isTuk } = useContext(AuthContext);
  const resolvedAlignLabel = alignLabel ?? "center";
  const resolvedTheme = theme ?? (isTuk ? "light" : "dark");
  const resolvedTextColor = style?.color ?? (disabled ? "#535457" : isTuk ? "#010205" : "#FBFCFF");

  return (
    <StyledPButtonPure
      isTuk={isTuk}
      active={active}
      alignLabel={resolvedAlignLabel}
      aria={aria}
      disabled={disabled}
      hideLabel={hideLabel}
      icon={icon}
      iconSource={iconSource}
      loading={loading}
      name={name}
      size={size}
      stretch={stretch}
      pdsTheme={resolvedTheme}
      type={type}
      underline={underline}
      value={value}
      ///
      suppressHydrationWarning={suppressHydrationWarning}
      autoFocus={autoFocus}
      className={className}
      dir={dir}
      hidden={hidden}
      id={id}
      lang={lang}
      slot={slot}
      style={style}
      tabIndex={tabIndex}
      title={title}
      translate={translate}
      //
      onClick={onClick}
    >
      <div
        style={{
          width: stretch ? "100%" : "min-content",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          color: resolvedTextColor,
        }}
      >
        {children}
      </div>
    </StyledPButtonPure>
  );
};

export default CButton;

